/**
 * @fileoverview `useSyncHtmlLang` — keeps `<html lang>` in step with the
 * live i18n instance.
 *
 * AAS-I18N-002 (2026-08-09). WCAG 2.1 **SC 3.1.1 Language of Page is
 * Level A**: the document's default human language must be
 * programmatically determinable, because assistive technology picks its
 * voice, pronunciation dictionary and hyphenation from `<html lang>`.
 *
 * Measured defect this hook closes: every authenticated app ships a
 * hardcoded `<html lang="en">` in `apps/<app>/index.html`, and the ONLY
 * code in the repo that ever wrote `document.documentElement.lang` was
 * `@puffin-core/marketing-shell`'s `<MarketingSeo>` — which is mounted on
 * the MARKETING portal only. So an authenticated surface rendering
 * Spanish was still announced `lang="en"`, i.e. read aloud by an English
 * TTS voice. The apps assert WCAG 2.1 AA conformance in their own in-app
 * help copy, so this was a false conformance claim as well as a defect.
 *
 * Why a hook and not a side effect inside `createAppI18n`: the factory
 * runs at module scope in BOTH web and React-Native bundles, and it also
 * runs in every unit suite. A React effect is the only place where the
 * write is (a) ordered after mount, (b) torn down with the tree, and
 * (c) trivially skippable on a platform with no DOM.
 *
 * SSR / React-Native safety: `palm-native` consumes this package and has
 * no `document`. The effect body no-ops when `document` is absent, and
 * this module imports NOTHING browser-only (same discipline as
 * `getInitialLanguage`, which reaches for the pure `./jwt` subpath so the
 * browser storage layer never enters the RN bundle).
 *
 * 🔴 `dir` IS written here as of AAS-I18N-002 ruling 11 (2026-08-10). This
 * docblock used to argue the opposite, and both of its reasons have been
 * removed rather than overridden:
 *
 *  1. "All advertised codes are LTR, so `dir` would be a constant `ltr`."
 *     True, and it is now a constant `ltr` FOR THOSE CODES — derived, not
 *     assumed. `SUPPORTED_LANGUAGES` carries `dir` per entry, so a real RTL
 *     locale (ar/he) is a one-row change with no new mechanism.
 *  2. "The RTL bundle's code is not a supported language, and a
 *     language-derived `dir` would overwrite the manual `rtl` that
 *     verification depends on." That manual `document.dir = 'rtl'` WAS the
 *     defect: a dev-only path, typed by hand, that nothing exercised. The
 *     bundle is now the first-class unadvertised pseudo-locale `en-XA`, so
 *     there is no manual write left to overwrite.
 *
 * ⚠️ THIS HOOK IS THE SINGLE WRITER of `documentElement.dir`.
 * `@puffin-core/page-shell`'s `useIsRtl` MutationObserves that attribute; a
 * second writer would race it. Extend this hook, never add another.
 *
 * @module @puffin-core/i18n-bootstrap/useSyncHtmlLang
 */

import { useEffect } from 'react';

import { directionForLanguage } from './localeMetadata.js';

/**
 * Mirror `i18n.resolvedLanguage` onto `document.documentElement.lang` AND
 * `…dir`, and keep both mirrored for the lifetime of the mount.
 *
 * Mount it once per authenticated app, beside the other bridge hooks
 * (`useSyncI18nFromAuth` / `useSyncI18nFromPreferences`). Mounting it more
 * than once in a tree is harmless — every instance writes the same value
 * and the write is skipped when the attribute already matches.
 *
 * @param {Object} i18n - i18next instance returned by `createAppI18n`.
 *   Read for `resolvedLanguage` (falling back to `language`), and
 *   subscribed to via `on('languageChanged')` when it exposes an emitter.
 * @returns {void}
 */
export function useSyncHtmlLang(i18n) {
  if (!i18n) {
    throw new Error('useSyncHtmlLang: i18n instance is required');
  }

  useEffect(() => {
    // React Native (palm-native) and any SSR pass have no document.
    if (typeof document === 'undefined' || !document.documentElement) {
      return undefined;
    }

    const applyLang = () => {
      const code = i18n.resolvedLanguage || i18n.language || null;
      if (!code) {
        return;
      }
      // `lang` and `dir` are written INDEPENDENTLY. An early return on a
      // matching `lang` would have skipped the `dir` write, which is exactly
      // the shape that leaves a page announced correctly and laid out
      // backwards after a locale swaps direction without changing tag.
      if (document.documentElement.getAttribute('lang') !== code) {
        document.documentElement.setAttribute('lang', code);
      }
      const dir = directionForLanguage(code);
      if (document.documentElement.getAttribute('dir') !== dir) {
        document.documentElement.setAttribute('dir', dir);
      }
    };

    // Write immediately so the boot language is reflected on first paint's
    // commit, not only after the first switch.
    applyLang();

    if (typeof i18n.on !== 'function') {
      return undefined;
    }
    i18n.on('languageChanged', applyLang);
    return () => {
      if (typeof i18n.off === 'function') {
        i18n.off('languageChanged', applyLang);
      }
    };
  }, [i18n]);
}

export default useSyncHtmlLang;
